// @ts-nocheck

// Lodash
import forEach from 'lodash/forEach';
import pick from 'lodash/pick';

// Misc / utils
import NEAR_ME_CONSTANTS from 'app/shared/constants/NearMeConstants';
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import SEO_CONSTANTS from 'app/shared/constants/SeoConstants';
import numberUtils from 'app/shared/utils/numberUtils';
import { areaUtils_forTypeOf } from 'app/shared/utils/areaUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import { filterUtils_getPropertyTypeFromFilterDescription } from 'app/shared/utils/filterUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import { LONGTAIL_SLUG_MAP } from 'app/shared/utils/searchSlugUtils';
import STATES from 'app/shared/utils/states.json';
import { areaUtils_isInSchoolZone } from 'app/shared/utils/areaUtils';

const _helpers = {
  getState: (property, item, wanted) => {
    for (let i = 0; i < STATES.length; i++) {
      if (STATES[i][property] === item) {
        return STATES[i][wanted];
      }
    }
  },
};

const _pageTitleHelpers = {
  getAreaName(area) {
    return area.type === 'zip' ? `Zip Code ${area.fullName}` : area.fullName;
  },
  getEfficiencyTitle({ isInSchoolZone, areaName, listingCount }) {
    if (isInSchoolZone) {
      return `Studios and Efficiency Apartments for Rent near ${areaName}${listingCount && listingCount >= 100 ? ` - ${listingCount} rentals` : ''} | HotPads`;
    }

    return `Studios and Efficiency Apartments for Rent in ${areaName}${listingCount && listingCount >= 100 ? ` - ${listingCount} rentals` : ''} | HotPads`;
  },
  getLowestPrice(listings = {}) {
    let lowPrice = 0;

    forEach(listings, (listing) => {
      if (listing && (!lowPrice || listing.listingMinMaxPriceBeds.minPrice < lowPrice)) {
        lowPrice = listing.listingMinMaxPriceBeds.minPrice;
      }
    });

    return lowPrice.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumSignificantDigits: 3,
    });
  },
  // Supports HPWEB-3592: Near me urls
  getNearMeTitle(pathname) {
    if (NEAR_ME_CONSTANTS.NEAR_ME_ROUTES[pathname]) {
      return NEAR_ME_CONSTANTS.NEAR_ME_ROUTES[pathname].title;
    }

    return 'Page not found | HotPads';
  },
  getPageTitlePriceRange({ listings, priceRange, preposition, searchSlug }) {
    const absoluteMinPrice = _pageTitleHelpers.getLowestPrice(listings);
    const weightedMinPrice = priceRange.minPrice; // weighted
    const weightedMaxPrice = priceRange.maxPrice; // weighted
    const zeroPriceRange = !weightedMinPrice && !weightedMaxPrice;
    const samePriceRange = weightedMinPrice === weightedMaxPrice;

    // If there is no weightedMinPrice or weightedMaxPrice range (zero listings)
    if (zeroPriceRange) {
      return null;
    }

    if (searchSlug === 'affordable-apartments-for-rent' || searchSlug === 'affordable-houses-for-rent') {
      return `${preposition} ${absoluteMinPrice}`;
    }

    // If there is only a single listing
    if (samePriceRange) {
      return `${preposition} ${numberUtils.compact(weightedMinPrice, true)}`;
    }

    return `${preposition} ${numberUtils.compact(weightedMinPrice, true)} to ${numberUtils.compact(
      weightedMaxPrice,
      true,
    )}+ a month`;
  },
  getPrepositions(type) {
    const areaSpecificPrepositions = areaUtils_forTypeOf[type];

    if (areaSpecificPrepositions) {
      return {
        main: areaSpecificPrepositions.prepositions.main,
        secondary: areaSpecificPrepositions.prepositions.secondary,
      };
    }

    // Fallback if an area specific preposition object does not exist
    return {
      main: 'near',
      secondary: 'from',
    };
  },
};

const _pageDescriptionHelpers = {
  getNearMeDescription(pathname) {
    if (NEAR_ME_CONSTANTS.NEAR_ME_ROUTES[pathname]) {
      return NEAR_ME_CONSTANTS.NEAR_ME_ROUTES[pathname].description;
    }

    return 'Sorry! The webpage you were trying to reach could not be found. Head to hotpads.com to start your search!';
  },
};

/**
 * Handles gathering Head data:
 * - Meta information (description)
 * - Description
 * - Links
 */
const useAreaHead = () => {
  const _getPageTitle = ({
    area,
    areaPriceRange,
    filterDescription,
    currentPage,
    listings,
    pathname,
    searchSlug = 'apartments-for-rent',
    totalListings,
  }) => {
    const schoolTypes = ['primaryschool', 'middleschool', 'mixedschool', 'highschool', 'university', 'unifschdist'];
    const isInSchoolZone = schoolTypes.includes(area.type);
    const isPageOne = currentPage === 1;

    // Page title building blocks
    const { main: mainPreposition, secondary: secondaryPreposition } = _pageTitleHelpers.getPrepositions(area.type);
    const priceRange = _pageTitleHelpers.getPageTitlePriceRange({
      listings,
      priceRange: areaPriceRange,
      preposition: secondaryPreposition,
      searchSlug,
    });
    const areaTypeAndName = _pageTitleHelpers.getAreaName(area);
    const slugDescription = filterDescription;
    const formattedTotalListings = numberUtils.fullNumber(totalListings);
    const showUnitCountOrPage = isPageOne
      ? `${totalListings && totalListings >= 100 ? `- ${formattedTotalListings} Rentals` : ''}`
      : `- Page ${currentPage}`;
    const showOrHidePage = isPageOne ? '' : `- Page ${currentPage}`;
    const showPriceOrPage = isPageOne ? priceRange : `- Page ${currentPage}`;
    const hotPads = '| HotPads';

    // Custom dynamic titles for near-me SEO pages
    const NEAR_ME_SEO_TITLES = {
      '/rentals-near-me': {
        withArea: (area, count) => `${area} rentals - ${count} | Hotpads.com`,
        withoutArea: (count) => `Rentals near me - ${count}`,
      },
      '/homes-for-rent-near-me': {
        withArea: (area, count) => `${area} homes for rent - ${count} rental homes | Hotpads.com`,
        withoutArea: (count) => `Homes for rent near me - ${count} rental homes | Hotpads.com`,
      },
      '/places-for-rent-near-me': {
        withArea: (area, count) => `Places for rent in ${area} - ${count} rental places | Hotpads.com`,
        withoutArea: (count) => `Places for rent near me - ${count} rental places | Hotpads.com`,
      },
      '/cheap-apartments-near-me': {
        withArea: (area, count) => `Cheap apartments in ${area} - ${count} apartments | Hotpads.com`,
        withoutArea: (count) => `Cheap apartments near me - ${count} apartments | Hotpads.com`,
      },
      '/1-bedroom-apartments-near-me': {
        withArea: (area, count) => `1 Bedroom apartments for rent in ${area} - ${count} 1 bed apartments | Hotpads.com`,
        withoutArea: (count) => `1 Bedroom apartments near me - ${count} 1 bed apartments | Hotpads.com`,
      },
    };

    const seoTitleConfig = NEAR_ME_SEO_TITLES[pathname];
    if (seoTitleConfig) {
      const formattedCount = totalListings ? numberUtils.fullNumber(totalListings) : '0';
      if (area && area.fullName) {
        return seoTitleConfig.withArea(area.fullName, formattedCount);
      }
      return seoTitleConfig.withoutArea(formattedCount);
    }

    // Supports HPWEB-3592: Near me urls
    const isNearMeUrl = routeUtils.isNearMeUrl(pathname);
    if (isNearMeUrl) {
      return _pageTitleHelpers.getNearMeTitle(pathname);
    }

    // Supports HPWEB-5392: Geo-target states that use the term "efficiency apartments" instead of "studio apartments"
    const efficiencyRegEx = new RegExp('(' + VALUE_CONSTANTS.STUDIO_EFFICIENCY_TEST_STATES + ')', 'i');
    const shouldUseEfficiencyTitle = efficiencyRegEx.test(area.state);
    if (shouldUseEfficiencyTitle && searchSlug === 'studio-apartments-for-rent') {
      return _pageTitleHelpers.getEfficiencyTitle({
        isInSchoolZone,
        areaName: area.fullName,
        listingCount: totalListings,
      });
    }

    // Supports HPWEB-5855
    const newApartmentsStarter = 'NEW Apartments Available Now';
    const newHousesStarter = 'Newest Homes Available Now';

    const PROPERTY_TYPE_FOR_RENT_BY_OWNER_FORMAT = [
      totalListings,
      filterDescription,
      mainPreposition,
      areaTypeAndName,
      hotPads,
    ];

    const HOUSES_FOR_RENT_BY_OWNER_FORMAT = [
      'Houses for Rent By Private Owner',
      `${totalListings} Rentals`,
      mainPreposition,
      areaTypeAndName,
    ];

    const APTS_AND_CONDOS_FOR_RENT_BY_OWNER_FORMAT = [
      'Apartments and Condos for Rent By Private Owner',
      `${totalListings} Rentals`,
      mainPreposition,
      areaTypeAndName,
    ];

    const pageTitleFormats = {
      'apartments-for-rent': [areaTypeAndName, slugDescription, showUnitCountOrPage, hotPads],
      'studio-apartments-for-rent': [slugDescription, mainPreposition, areaTypeAndName, showUnitCountOrPage, hotPads],
      'affordable-apartments-for-rent': [slugDescription, mainPreposition, areaTypeAndName, showPriceOrPage, hotPads],
      'affordable-houses-for-rent': [slugDescription, mainPreposition, areaTypeAndName, showPriceOrPage, hotPads],
      'houses-for-rent': [slugDescription, mainPreposition, areaTypeAndName, showOrHidePage, hotPads],
      // For Rent by Private Owner 159 Rentals in Richmond, VA
      'for-rent-by-owner': ['For Rent by Private Owner', `${totalListings} Rentals`, mainPreposition, areaTypeAndName],
      // Houses for Rent by Private Owner 59 Rentals in Richmond, VA
      'houses-for-rent-by-owner': HOUSES_FOR_RENT_BY_OWNER_FORMAT,
      // Apartments for Rent by Private Owner 59 Rentals in Richmond, VA
      'apartments-and-condos-for-rent-by-owner': APTS_AND_CONDOS_FOR_RENT_BY_OWNER_FORMAT,
      'townhomes-for-rent-by-owner': PROPERTY_TYPE_FOR_RENT_BY_OWNER_FORMAT,
      'new-apartments-for-rent': [newApartmentsStarter, mainPreposition, areaTypeAndName],
      'new-houses-for-rent': [newHousesStarter, mainPreposition, areaTypeAndName],
      sublets: [areaTypeAndName, 'Sublets', showUnitCountOrPage, hotPads],
      'student-housing': [areaTypeAndName, slugDescription, showUnitCountOrPage, hotPads],
      default: [filterDescription, mainPreposition, areaTypeAndName, showUnitCountOrPage, hotPads],
    };

    // If we're in a school zone
    if (isInSchoolZone) {
      const city = area.city;
      const state = city ? area.state : _helpers.getState('abbreviation', area.state, 'name');
      const schoolLocation = `${city ? `${city}, ${state}` : `${state}`}`;

      if (searchSlug === 'for-rent-by-owner') {
        pageTitleFormats[searchSlug].splice(4, 0, schoolLocation);
        return pageTitleFormats[searchSlug].join(' ');
      }

      if (searchSlug === 'affordable-apartments-for-rent' || searchSlug === 'affordable-houses-for-rent') {
        pageTitleFormats[searchSlug].splice(3, 0, schoolLocation);
        return pageTitleFormats[searchSlug].join(' ');
      }

      pageTitleFormats.default.splice(3, 0, schoolLocation);
      return pageTitleFormats.default.join(' ');
    }

    const pageTitle = (pageTitleFormats[searchSlug] || pageTitleFormats.default).filter(Boolean);

    return pageTitle.join(' ');
  };

  const _getPageDescription = ({ area, listings, pathname, searchSlug, filterDescription, totalListings }) => {
    const areaTypeMap = {
      county: 'city',
      university: 'school',
      unifschdist: 'school',
      primaryschool: 'school',
      middleschool: 'school',
      highschool: 'school',
      mixedschool: 'school',
      elemschdist: 'school',
      secschdist: 'school',
    };

    const areaType = areaTypeMap[area.type] || area.type;
    const propertyType = filterUtils_getPropertyTypeFromFilterDescription(filterDescription);
    const areaName = area.fullName;
    const shortenedAreaName = areaName && areaName.slice(0, -4);
    const city = area.city;
    const state = city ? area.state : _helpers.getState('abbreviation', area.state, 'name');
    const schoolLocation = `${city ? `${city}, ${state}` : `${state}`}`;
    const absoluteMinPrice = _pageTitleHelpers.getLowestPrice(listings);

    // Supports HPWEB-3592
    const isNearMeUrl = routeUtils.isNearMeUrl(pathname);
    if (isNearMeUrl) {
      return _pageDescriptionHelpers.getNearMeDescription(pathname);
    }

    const descSuffix = {
      1: 'on the largest and most trusted rental site. View detailed property information with 3D Tours and real-time updates.',
      2: 'with the largest and most trusted rental site. View detailed property information with 3D Tours and real-time updates.',
      3: 'with the largest and most trusted rental site. View detailed home information and request to immediately apply online!',
      4: 'by searching your desired city, neighborhood or school with the largest and most trusted rental site.',
      5: 'Be the first to contact!',
    };

    // Generate under-price longtail descriptions programmatically
    const underPriceTypes = [
      { slugPrefix: 'houses', typeLabel: 'houses', prices: [500, 1000, 1500, 2000, 2500, 3000] },
      { slugPrefix: 'apartments', typeLabel: 'apartments', prices: [500, 1000, 1500, 2000, 2500, 3000] },
      { slugPrefix: 'condos', typeLabel: 'condos', prices: [500, 1000, 1500, 2000, 2500, 3000] },
      { slugPrefix: 'duplexes', typeLabel: 'duplexes', prices: [500, 1000, 1500, 2000, 2500, 3000] },
      { slugPrefix: 'rooms-for-rent', typeLabel: 'rooms', prices: [500, 1000, 1500, 2000] },
    ];
    const underPriceLongtail: Record<string, Record<string, string>> = {};
    for (const { slugPrefix, typeLabel, prices } of underPriceTypes) {
      for (const price of prices) {
        const fp = `$${price.toLocaleString('en-US')}`;
        underPriceLongtail[`${slugPrefix}-under-${price}`] = {
          city: `Search ${typeLabel} for rent under ${fp} in ${areaName} ${descSuffix[1]}`,
          school: `Search ${typeLabel} for rent under ${fp} near ${areaName} in ${schoolLocation} ${descSuffix[1]}`,
          neighborhood: `Search ${typeLabel} for rent under ${fp} in the ${shortenedAreaName} neighborhood ${descSuffix[1]}`,
          zip: `Search ${typeLabel} for rent under ${fp} within the ${areaName} zip code ${descSuffix[1]}`,
          state: `Browse ${typeLabel} for rent under ${fp} in the state of ${areaName} ${descSuffix[4]}`,
        };
      }
    }

    const longtailDescriptions = {
      'apartments-for-rent-with-laundry': {
        city: `Search apartments for rent with laundry in ${areaName} ${descSuffix[1]}`,
        school: `Search apartments for rent with laundry near ${areaName} in ${schoolLocation} ${descSuffix[1]}`,
        neighborhood: `Search apartments for rent with laundry in the ${shortenedAreaName} neighborhood ${descSuffix[1]}`,
        zip: `Search apartments for rent with laundry within the ${areaName} zip code ${descSuffix[1]}`,
        state: `Browse apartments for rent with laundry in the state of ${areaName} ${descSuffix[4]}`,
      },
      'apartments-for-rent-with-parking': {
        city: `Search apartments for rent with dedicated parking in ${areaName} ${descSuffix[1]}`,
        school: `Search apartments for rent with dedicated parking near ${areaName} in ${schoolLocation} ${descSuffix[1]}`,
        neighborhood: `Search apartments for rent with dedicated parking in the ${shortenedAreaName} neighborhood ${descSuffix[1]}`,
        zip: `Search apartments for rent with dedicated parking within the ${areaName} zip code ${descSuffix[1]}`,
        state: `Browse apartments for rent with dedicated parking in the state of ${areaName} ${descSuffix[4]}`,
      },
      'apartments-for-rent-with-gym': {
        city: `Search apartments for rent with workout facilities in ${areaName} ${descSuffix[1]}`,
        school: `Search apartments for rent with workout facilities near ${areaName} in ${schoolLocation} ${descSuffix[1]}`,
        neighborhood: `Search apartments for rent with workout facilities in the ${shortenedAreaName} neighborhood ${descSuffix[1]}`,
        zip: `Search apartments for rent with workout facilities within the ${areaName} zip code ${descSuffix[1]}`,
        state: `Browse apartments for rent with workout facilities in the state of ${areaName} ${descSuffix[4]}`,
      },
      'affordable-apartments-for-rent': {
        city: `Search affordable apartments for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search affordable apartments for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search affordable apartments for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search affordable apartments for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse affordable apartments for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'luxury-apartments-for-rent': {
        city: `Search high-end apartments for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search high-end apartments for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search high-end apartments for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search high-end apartments for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse high-end apartments for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'pet-friendly-apartments-for-rent': {
        city: `Search dog and cat friendly apartments for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search dog and cat friendly apartments for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search dog and cat friendly apartments for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search dog and cat friendly apartments for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse dog and cat friendly apartments for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'for-rent-by-owner': {
        city: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses, apartments, condos, and townhomes near you.`,
        school: `Search FRBO properties near ${areaName} in ${schoolLocation} ${descSuffix[3]}`,
        neighborhood: `Search FRBO properties near ${shortenedAreaName} neighborhood ${descSuffix[3]}`,
        zip: `Search FRBO properties within the ${areaName} zip code ${descSuffix[3]}`,
        state: `Browse FRBO properties in the state of ${areaName} ${descSuffix[4]}`,
      },
      'houses-for-rent-by-owner': {
        city: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses near you with 1 to 4+ bed/baths, pet friendly, and more.`,
        school: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses near you with 1 to 4+ bed/baths, pet friendly, and more.`,
        neighborhood: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses near you with 1 to 4+ bed/baths, pet friendly, and more.`,
        zip: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses near you with 1 to 4+ bed/baths, pet friendly, and more.`,
        state: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO houses near you with 1 to 4+ bed/baths, pet friendly, and more.`,
      },
      'apartments-and-condos-for-rent-by-owner': {
        city: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO apartments near you with 1 to 4+ bed/bath, pet friendly, and more.`,
        school: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO apartments near you with 1 to 4+ bed/bath, pet friendly, and more.`,
        neighborhood: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO apartments near you with 1 to 4+ bed/bath, pet friendly, and more.`,
        zip: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO apartments near you with 1 to 4+ bed/bath, pet friendly, and more.`,
        state: `Find your new home starting from ${absoluteMinPrice}/mo. FRBO apartments near you with 1 to 4+ bed/bath, pet friendly, and more.`,
      },
      'townhomes-for-rent-by-owner': {
        city: `FRBO townhomes near me. Browse the largest rental inventory of private owner listings.`,
        school: `FRBO townhomes near me. Browse the largest rental inventory of private owner listings.`,
        neighborhood: `FRBO townhomes near me. Browse the largest rental inventory of private owner listings.`,
        zip: `FRBO townhomes near me. Browse the largest rental inventory of private owner listings.`,
        state: `FRBO townhomes near me. Browse the largest rental inventory of private owner listings.`,
      },
      'rooms-for-rent': {
        city: `Search available rooms for rent in ${areaName} ${descSuffix[3]}`,
        school: `Search available rooms for rent near ${areaName} in ${schoolLocation} ${descSuffix[3]}`,
        neighborhood: `Search available rooms for rent near ${shortenedAreaName} neighborhood ${descSuffix[3]}`,
        zip: `Search available rooms for rent within the ${areaName} zip code ${descSuffix[3]}`,
        state: `Browse available rooms for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      sublets: {
        city: `Search available sublets in ${areaName} ${descSuffix[3]}`,
        school: `Search available sublets near ${areaName} in ${schoolLocation} ${descSuffix[3]}`,
        neighborhood: `Search available sublets near ${shortenedAreaName} neighborhood ${descSuffix[3]}`,
        zip: `Search available sublets within the ${areaName} zip code ${descSuffix[3]}`,
        state: `Browse available sublets in the state of ${areaName} ${descSuffix[4]}`,
      },
      'new-apartments-for-rent': {
        city: `Search the most recent apartment rentals to hit the market in the last 24 hours in ${areaName}. ${descSuffix[5]}`,
        school: `Search the most recent apartment rentals to hit the market in the last 24 hours near ${areaName} in ${schoolLocation}. ${descSuffix[5]}`,
        neighborhood: `Search the most recent apartment rentals to hit the market in the last 24 hours in the ${shortenedAreaName} neighborhood. ${descSuffix[5]}`,
        zip: `Search the most recent apartment rentals to hit the market in the last 24 hours within the ${areaName} zip code. ${descSuffix[5]}`,
        state: `Browse the most recent apartment rentals to hit the market in the last 24 hours in the state of ${areaName}. ${descSuffix[4]}`,
      },
      'new-houses-for-rent': {
        city: `Search the most recent rental houses to hit the market in the last 24 hours in ${areaName}. ${descSuffix[5]}`,
        school: `Search the most recent rental houses to hit the market in the last 24 hours near ${areaName} in ${schoolLocation}. ${descSuffix[5]}`,
        neighborhood: `Search the most recent rental houses to hit the market in the last 24 hours in the ${shortenedAreaName} neighborhood. ${descSuffix[5]}`,
        zip: `Search the most recent rental houses to hit the market in the last 24 hours within the ${areaName} zip code. ${descSuffix[5]}`,
        state: `Browse the most recent rental houses to hit the market in the last 24 hours in the state of ${areaName}. ${descSuffix[4]}`,
      },
      'duplexes-for-rent': {
        city: `Search duplexes for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search duplexes for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search duplexes for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search duplexes for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse duplexes for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'furnished-duplexes-for-rent': {
        city: `Search furnished duplexes for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search furnished duplexes for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search furnished duplexes for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search furnished duplexes for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse furnished duplexes for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'pet-friendly-duplexes-for-rent': {
        city: `Search dog and cat friendly duplexes for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search dog and cat friendly duplexes for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search dog and cat friendly duplexes for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search dog and cat friendly duplexes for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse dog and cat friendly duplexes for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      'corporate-housing-for-rent': {
        city: `Search corporate housing for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search corporate housing for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search corporate housing for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search corporate housing for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse corporate housing for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      '4-bedroom-apartments-for-rent': {
        city: `Search 4 bedroom apartments for rent in ${areaName} ${descSuffix[1]}`,
        school: `Search 4 bedroom apartments for rent near ${areaName} in ${schoolLocation} ${descSuffix[1]}`,
        neighborhood: `Search 4 bedroom apartments for rent in the ${shortenedAreaName} neighborhood ${descSuffix[1]}`,
        zip: `Search 4 bedroom apartments for rent within the ${areaName} zip code ${descSuffix[1]}`,
        state: `Browse 4 bedroom apartments for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
      ...underPriceLongtail,
      default: {
        city: `Search ${propertyType} for rent in ${areaName} ${descSuffix[2]}`,
        school: `Search ${propertyType} for rent near ${areaName} in ${schoolLocation} ${descSuffix[2]}`,
        neighborhood: `Search ${propertyType} for rent in the ${shortenedAreaName} neighborhood ${descSuffix[2]}`,
        zip: `Search ${propertyType} for rent within the ${areaName} zip code ${descSuffix[2]}`,
        state: `Browse ${propertyType} for rent in the state of ${areaName} ${descSuffix[4]}`,
      },
    };

    // Use slug-specific description even when totalListings is 0, except for
    // by-owner slugs whose descriptions depend on absoluteMinPrice (listing data).
    const longtailDescription = longtailDescriptions[searchSlug]?.[areaType];
    if (longtailDescription && !searchSlug.includes('by-owner')) {
      return longtailDescription;
    }

    if (totalListings === 0) {
      return `Search ${propertyType} in ${areaName} and nearby with the largest and most trusted rental site. Search by commute times, street view and receive real-time updates.`;
    }

    // By-owner slugs with listings get their longtail description here;
    // slugs without a longtail entry fall through to the default.
    return longtailDescription || longtailDescriptions.default[areaType];
  };

  const getHeaderDetails = ({
    area,
    areaPriceRange,
    searchSlug,
    filterDescription,
    currentPage,
    listings,
    pathname,
    totalListings,
  }) => {
    const pageTitle = _getPageTitle({
      area,
      areaPriceRange,
      filterDescription,
      currentPage,
      listings,
      pathname,
      searchSlug,
      totalListings,
    });
    const pageDescription = _getPageDescription({
      area,
      listings,
      pathname,
      searchSlug,
      filterDescription,
      totalListings,
    });

    return {
      pageDescription,
      pageTitle,
      searchSlug,
    };
  };

  const getNextUrl = ({ areaState, pathname, page, maxPage, query }) => {
    const newQuery = Object.assign({}, query);
    page = Number(page);
    maxPage = Number(maxPage);
    const nextPage = page + 1;
    const hasNextPage = !routeUtils.isNearMeUrl(pathname) && nextPage <= maxPage;
    const noPrevNextRegEx = new RegExp('(' + VALUE_CONSTANTS.NO_PREV_NEXT_TEST_STATES + ')', 'i');
    const shouldNoPrevNext = noPrevNextRegEx.test(areaState);

    if (hasNextPage && !shouldNoPrevNext) {
      newQuery.page = nextPage;
      return (
        'https://hotpads.com' + routeUtils.buildPathWithQuery(pathname, pick(newQuery, SEO_CONSTANTS.allowedUrlParams))
      );
    } else {
      return false;
    }
  };

  const getPrevUrl = ({ areaState, pathname, page, query }) => {
    const newQuery = Object.assign({}, query);
    page = Number(page);
    const hasPrevPage = page > 1;
    const noPrevNextRegEx = new RegExp('(' + VALUE_CONSTANTS.NO_PREV_NEXT_TEST_STATES + ')', 'i');
    const shouldNoPrevNext = noPrevNextRegEx.test(areaState);

    if (hasPrevPage && !shouldNoPrevNext) {
      delete newQuery.page;
      const prevPage = page - 1;
      const isNotFirstPage = prevPage > 1;

      if (isNotFirstPage) {
        newQuery.page = prevPage;
      }
      return (
        'https://hotpads.com' + routeUtils.buildPathWithQuery(pathname, pick(newQuery, SEO_CONSTANTS.allowedUrlParams))
      );
    } else {
      return false;
    }
  };

  const getCanonicalUrl = ({ areaState, areaType, page, pathname, query, url: canonicalUrl }) => {
    canonicalUrl = canonicalUrl.split('?')[0];
    const queryKeys = Object.keys(query);
    const sanitizedParams = queryKeys.map((key) => {
      return `${key}=${query[key]}`;
    });
    const sanitizedPathname = pathname && pathname.split('/').filter(Boolean);
    const resourceId = sanitizedPathname && sanitizedPathname[0];
    const slug = sanitizedPathname && sanitizedPathname[1];
    const isInSchoolZone = areaUtils_isInSchoolZone(areaType);
    const longtailRedirects =
      sanitizedParams &&
      sanitizedParams
        .map((param) => {
          if (LONGTAIL_SLUG_MAP[slug] && LONGTAIL_SLUG_MAP[slug][param] && !isInSchoolZone) {
            return LONGTAIL_SLUG_MAP[slug][param];
          }
        })
        .filter(Boolean);

    // Check if there is only one longtail redirect; if true, use the redirect, else default
    // to standard `apartments-for-rent` or `houses-for-rent`
    const shouldModifyCanonical = Boolean(longtailRedirects && longtailRedirects.length === 1);
    const noPrevNextRegEx = new RegExp('(' + VALUE_CONSTANTS.NO_PREV_NEXT_TEST_STATES + ')', 'i');
    const shouldNoPrevNext = noPrevNextRegEx.test(areaState);

    if (shouldModifyCanonical) {
      const redirectSlug = longtailRedirects.pop();
      canonicalUrl = `https://hotpads.com/${resourceId}/${redirectSlug}`;
    }

    if (canonicalUrl) {
      const pagination = page >= 2 ? { page } : {};
      const search = queryUtils.stringify(pagination);

      return shouldNoPrevNext ? canonicalUrl : canonicalUrl + search;
    }

    return false;
  };

  const shouldAreaHeadNoIndex = ({ page, maxPage }) => {
    if (page > maxPage || page < 0) {
      return true;
    }
    return false;
  };

  const shouldAreaHeadNoIndexNoFollow = ({ resourceId, areaType }) => {
    if (resourceId === 'united-states' || areaType === 'country') {
      return true;
    }
    return false;
  };

  return {
    getHeaderDetails,
    getNextUrl,
    getPrevUrl,
    getCanonicalUrl,
    shouldAreaHeadNoIndex,
    shouldAreaHeadNoIndexNoFollow,
  };
};

export default useAreaHead;
